import useAxios from '@/composables/useAxios';
import TaskToDoApiResponse from '@/interfaces/Api/TaskToDoApiResponse';
import { ref, watch, WatchStopHandle } from 'vue';
import { Router } from 'vue-router';

const currentTaskConfig = ref<TaskToDoApiResponse | null>(null);

/**
 * Automatically change the task view when the task configuration changes.
 * Must be called once, with the app router (a watcher per useTaskProgress() call would navigate several times).
 */
export function watchTaskProgressNavigation(router: Pick<Router, 'push'>): WatchStopHandle {
    return watch(currentTaskConfig, (newConfig) => {
        if (!newConfig) {
            return;
        }
        if (newConfig.status === 'SESSION_ENDED') {
            router.push({
                name: `homepage`,
            });
        } else if (newConfig.status === 'TASK_TODO') {
            // PEM/END system tasks now also carry a real (non-editable) task_id (#470), so task_id
            // truthiness alone no longer distinguishes them from MAT — `is_legacy` (no DB-backed
            // task_config) is the reliable signal for "hardcoded PEM/END view" vs "generic milieu".
            if (newConfig.task_id && !newConfig.is_legacy) {
                router.push({
                    name: 'tasks.milieu',
                    params: { taskId: String(newConfig.task_id) },
                });
            } else {
                router.push({
                    name: `tasks.${newConfig.task_code}`,
                });
            }
        }
    });
}

/**
 * This composable manages the student's progress by switching the task view depending
 * on the task configuration received from the API.
 */
export default function useTaskProgress() {
    const axios = useAxios();

    /** `timeoutMs` bounds the request (eprocom-pwa#857: the pull that follows an upload). Unset: unbounded. */
    async function getTaskToDo(timeoutMs?: number): Promise<TaskToDoApiResponse | null> {
        try {
            const response =
                timeoutMs !== undefined
                    ? await axios.get('student/progress/task', { timeout: timeoutMs })
                    : await axios.get('student/progress/task');
            const taskConfigResponse = response.data as TaskToDoApiResponse;
            currentTaskConfig.value = taskConfigResponse;
            return taskConfigResponse;
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    /**
     * Allows to manually set the current task configuration when joining the test session, for example.
     * @param taskConfig
     */
    function setCurrentTaskConfig(taskConfig: TaskToDoApiResponse | null) {
        currentTaskConfig.value = taskConfig;
    }

    return {
        getTaskToDo,
        setCurrentTaskConfig,
        currentTaskConfig,
    };
}
